import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Package, ShoppingBag, Users, Mail, ArrowLeft, LogOut } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <div className="p-12 text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (user.role !== "admin")
    return (
      <div className="max-w-xl mx-auto p-12 text-center">
        <h1 className="font-display text-3xl text-white">Admin only</h1>
        <p className="mt-3 text-white/60">You don't have access to this area. Sign in with an admin account.</p>
        <Link to="/" className="mt-6 inline-block btn-ghost-violet h-11 px-6 rounded-md leading-[2.5rem]">Back home</Link>
      </div>
    );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:shrink-0 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/5 bg-[#0A0812] p-6">
        <Link to="/" className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-mono inline-flex items-center gap-2 hover:text-white">
          <ArrowLeft size={12} /> Back to store
        </Link>
        <div className="mt-6 mb-8">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Admin</div>
          <div className="font-display font-extrabold text-2xl text-white tracking-tighter">Control Vault</div>
          <div className="text-xs text-white/50 mt-1">{user.email}</div>
        </div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
                  isActive
                    ? "bg-[#B026FF]/15 text-white border border-[#B026FF]/30"
                    : "text-white/70 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="mt-8 w-full text-left text-sm text-white/50 hover:text-white flex items-center gap-2 px-3 py-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </aside>
      <main className="flex-1 p-6 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
}
